/**
 * v2.71.96 — Notification Bell + Center.
 *
 * A single self-contained component drop into any header (public Header,
 * dashboard headers, OfficeLayout). It owns:
 *   • The bell icon with the unread count badge.
 *   • The dropdown panel with 5 tabs (All / Leads / Properties / Visits / System).
 *   • Mark-as-read, Mark-all-read, Delete actions.
 *
 * It uses the `useNotifications()` hook for live updates over WebSocket
 * (with REST fallback). No other components in the app need to know about
 * notification plumbing.
 *
 * Persistence — everything lives server-side in MongoDB; refreshing,
 * logging out, or switching devices preserves the inbox.
 *
 * Visibility — the bell renders only when `user.role` is in
 * { client, dealer, builder, admin }. Guests and team members see nothing.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Trash2, X, AlertCircle, Inbox, ChevronRight } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useNotifications } from "../lib/useNotifications";

const ROLE_ALLOWED = new Set(["client", "dealer", "builder", "admin"]);

const TABS = [
  { key: "all",       label: "All",        match: () => true },
  { key: "lead",      label: "Leads",      match: (n) => n.notification_type === "lead" },
  { key: "property",  label: "Properties", match: (n) => n.notification_type === "property" },
  { key: "visit",     label: "Visits",     match: (n) => n.notification_type === "visit" },
  { key: "system",   label: "System",     match: (n) => n.notification_type === "system" },
];

/**
 * Props:
 *   - variant: "light" | "dark"   (default "light" — for public/dashboards; "dark" for Office)
 *   - className: additional wrapper class
 */
export default function NotificationBell({ variant = "light", className = "" }) {
  const { user } = useAuth();
  const enabled = user && ROLE_ALLOWED.has(user.role);
  const { items, unread, markRead, markAllRead, remove, refresh } = useNotifications({ enabled: Boolean(enabled) });

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) && !btnRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const t = TABS.find((x) => x.key === tab) || TABS[0];
    return items.filter(t.match);
  }, [items, tab]);

  if (!enabled) return null;

  const isDark = variant === "dark";
  const surface = isDark ? "bg-[#181818] border-[#2a2a2a] text-white" : "bg-white border-[#E4E4E7] text-[#0F0F0F]";
  const muted = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";
  const tabActiveBg = isDark ? "bg-[#FFB200] text-[#1A0F00]" : "bg-[#FFB200] text-[#1A0F00]";
  const tabIdleBg = isDark ? "text-[#9CA3AF] hover:bg-[#222]" : "text-[#52525B] hover:bg-[#F4F4F5]";
  const triggerColor = isDark ? "text-white hover:bg-white/10" : "text-[#27272A] hover:bg-[#F4F4F5]";

  return (
    <div className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative h-9 w-9 rounded-md inline-flex items-center justify-center transition ${triggerColor}`}
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-[18px] min-w-[18px] px-[5px] rounded-full bg-rose-500 text-white text-[10px] font-bold inline-flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          className={`fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-auto sm:mt-2 w-auto sm:w-[420px] max-w-[460px] mx-auto sm:mx-0 max-h-[calc(100vh-80px)] sm:max-h-[640px] flex flex-col rounded-lg shadow-2xl border z-50 ${surface}`}
          data-testid="notification-center"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: isDark ? "#2a2a2a" : "#E4E4E7" }}>
            <div>
              <div className="text-[14px] font-bold inline-flex items-center gap-1.5">
                <Bell size={14} /> Notifications
              </div>
              <div className={`text-[11px] mt-0.5 ${muted}`}>
                {unread === 0 ? "All caught up" : `${unread} unread`}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className={`text-[11.5px] font-semibold px-2 py-1 rounded transition ${isDark ? "text-[#FFB200] hover:bg-white/5" : "text-[#B4540E] hover:bg-orange-50"}`}
                  data-testid="notification-mark-all-read"
                  title="Mark all as read"
                >
                  <CheckCheck size={12} className="inline mr-0.5" /> Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className={`h-7 w-7 rounded inline-flex items-center justify-center ${isDark ? "hover:bg-white/10" : "hover:bg-[#F4F4F5]"}`}
                aria-label="Close"
              >
                <X size={13} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-2 py-2 flex items-center gap-1 overflow-x-auto border-b" style={{ borderColor: isDark ? "#2a2a2a" : "#E4E4E7" }}>
            {TABS.map((t) => {
              const count = items.filter(t.match).filter((n) => !n.read_status).length;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex-shrink-0 h-7 px-3 rounded-md text-[12px] font-semibold transition inline-flex items-center gap-1 ${tab === t.key ? tabActiveBg : tabIdleBg}`}
                  data-testid={`notification-tab-${t.key}`}
                >
                  {t.label}
                  {count > 0 && (
                    <span
                      className="text-[10px] font-bold h-4 min-w-[16px] px-1 rounded-full inline-flex items-center justify-center"
                      style={{ background: tab === t.key ? "rgba(0,0,0,0.18)" : (isDark ? "#FFB200" : "#FFB200"), color: tab === t.key ? "#1A0F00" : "#1A0F00" }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto" data-testid="notification-list">
            {filtered.length === 0 ? (
              <div className="p-10 text-center" data-testid="notification-empty">
                <Inbox size={28} className={`mx-auto ${muted} mb-2`} />
                <p className={`text-[13px] ${muted}`}>No notifications yet.</p>
              </div>
            ) : (
              <ul>
                {filtered.map((n) => (
                  <NotificationRow
                    key={n.id}
                    n={n}
                    isDark={isDark}
                    onMarkRead={() => markRead(n.id)}
                    onRemove={() => remove(n.id)}
                    onNavigated={() => setOpen(false)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Single notification row ──────────────────────────────────────── */

function NotificationRow({ n, isDark, onMarkRead, onRemove, onNavigated }) {
  const nav = useNavigate();
  const dotColor = ({
    lead: "bg-amber-500",
    property: "bg-sky-500",
    visit: "bg-violet-500",
    system: "bg-slate-400",
  }[n.notification_type] || "bg-slate-400");

  const handleOpen = () => {
    if (!n.read_status) onMarkRead();
    if (n.action_url) {
      nav(n.action_url);
      onNavigated?.();
    }
  };

  return (
    <li
      className={`relative border-b ${isDark ? "border-[#222]" : "border-[#F4F4F5]"} ${!n.read_status ? (isDark ? "bg-white/[0.03]" : "bg-amber-50/40") : ""}`}
      data-testid={`notification-row-${n.id}`}
    >
      <div className="px-4 py-3 flex gap-3">
        <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${dotColor}`} />
        <button
          onClick={handleOpen}
          className="flex-1 text-left min-w-0"
          data-testid={`notification-open-${n.id}`}
        >
          <div className="text-[13.5px] font-semibold leading-tight mb-0.5 line-clamp-1">{n.title}</div>
          <div className={`text-[12px] leading-snug line-clamp-2 ${isDark ? "text-[#C7C7C7]" : "text-[#3F3F46]"}`}>
            {n.message}
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <span className={`text-[10.5px] font-medium uppercase tracking-wider ${isDark ? "text-[#9CA3AF]" : "text-[#71717A]"}`}>
              {n.notification_type}
            </span>
            <span className={`text-[10.5px] ${isDark ? "text-[#71717A]" : "text-[#A1A1AA]"}`}>
              · {relTime(n.created_at)}
            </span>
            {!n.read_status && (
              <span className="text-[10.5px] font-bold text-rose-500">· NEW</span>
            )}
            {n.action_url && (
              <span className={`text-[10.5px] inline-flex items-center gap-0.5 ${isDark ? "text-[#FFB200]" : "text-[#B4540E]"}`}>
                · Open <ChevronRight size={10} />
              </span>
            )}
          </div>
        </button>
        <div className="flex flex-col gap-1 flex-shrink-0">
          {!n.read_status && (
            <button
              onClick={onMarkRead}
              className={`h-7 w-7 rounded inline-flex items-center justify-center ${isDark ? "hover:bg-white/10 text-[#9CA3AF]" : "hover:bg-[#F4F4F5] text-[#52525B]"}`}
              title="Mark as read"
              data-testid={`notification-mark-read-${n.id}`}
            >
              <Check size={13} />
            </button>
          )}
          <button
            onClick={onRemove}
            className={`h-7 w-7 rounded inline-flex items-center justify-center ${isDark ? "hover:bg-rose-500/20 text-[#9CA3AF]" : "hover:bg-rose-50 text-[#52525B]"}`}
            title="Delete"
            data-testid={`notification-delete-${n.id}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </li>
  );
}

/* ─── Time formatter ───────────────────────────────────────────────── */

function relTime(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  } catch (_) { return ""; }
}
